import { inject, injectable } from 'inversify';
import { type IDispatcher } from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr';
import DITypes from '@creditflowresearch/json-rpc-dispatcher/dispatcher-cfr/DITypes';
import Request from '@framework/api/Rpc/Request';
import { IUserPlanRequestsState } from '@/creditflowresearch/application/User/UserPlan/types';
import { IRequestServicePayload } from '@/creditflowresearch/domain/vo/IRequestServicePayload';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import {
  IManageUserPlanApproveDenyPayload,
  IManagePlansRequestPayload,
  IManagePlansRequestResponse,
  IManagePlansSubscribersResponse,
  IManagePlansSubscribersPayload,
  IManagePlansManageSubscriberPayload,
  IAddSubscribersPayload,
} from '@/condor/domain/vo/FinsightManager/ManagePlans';
import { IUserPlanResponse } from '@creditflowresearch/user-plan/vo/IUserPlanResponse';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import { UpdateSubscriptionPreferencesPayload } from '@/creditflowresearch/domain/vo/Subscriptions';
import {
  IContactSettingsPreferences,
  ISaveContactSettingsPayload,
} from '@/creditflowresearch/domain/vo/ContactSettings';

@injectable()
class ContactRepository {
  constructor(
    @inject(DITypes.JsonRpcDispatcher) protected readonly rpc: IDispatcher,
  ) {}

  getUserCurrentPlan = async (payload = {}): Promise<IUserPlanResponse> => {
    const request = new Request('finsight_web.contact.subscription.get_current_plan', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getUserPlanById = async (payload: { contactId: string }) => {
    const request = new Request('finsight_web.contact.subscription.get_current_plan_by_contact_id', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getSubscriptionRequests = async (payload = {}): Promise<IUserPlanRequestsState> => {
    const request = new Request('finsight_web.contact.subscription.get_requests', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getSubscribersList = async (payload: IManagePlansSubscribersPayload): Promise<IManagePlansSubscribersResponse> => {
    const request = new Request('finsight_web.contact.subscription.get_subscribers', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  updateUserSubscription = async (payload: IManagePlansManageSubscriberPayload) => {
    const request = new Request('finsight_web.contact.subscription.manage_subscription', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getContactSettingsPreferences = async (payload = {}): Promise<IContactSettingsPreferences> => {
    const request = new Request('cfr.contact_settings.get_preferences', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  saveContactSettings = async (payload: ISaveContactSettingsPayload): Promise<null> => {
    const request = new Request('cfr.contact_settings.save', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  updateUserSubscriptionPlanPreferences = async (
    payload: UpdateSubscriptionPreferencesPayload,
  ): Promise<null> => {
    const request = new Request('finsight_web.contact.subscription.manage_preferences', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getCondorSubscriptionRequests = async (payload: IManagePlansRequestPayload): Promise<IManagePlansRequestResponse> => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request('finsight_web.contact.subscription.manager.get_subscription_requests', payload),
    );
    return response.getResult().payload;
  };

  approveUserRequest = async (payload: IManageUserPlanApproveDenyPayload) => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request('finsight_web.contact.subscription.approve_subscription_request', payload),
    );
    return response.getResult().payload;
  };

  denyUserRequest = async (payload: IManageUserPlanApproveDenyPayload) => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request('finsight_web.contact.subscription.deny_subscription_request', payload),
    );
    return response.getResult().payload;
  };

  requestService = async (payload: IRequestServicePayload) => {
    let response = await this.rpc.call<RpcSuccess>(new Request(
      'finsight_web.contact.subscription.request_service',
      payload,
    ));

    return response.getResult().payload;
  };

  getPendingRequestsCount = async ():Promise<{ count: number }> => {
    const response = await this.rpc.call<RpcSuccess>(
      new Request('finsight_web.contact.subscription.get_pending_request_count'),
    );
    return response.getResult().payload;
  };

  addSubscribersByAdmin = async (payload: IAddSubscribersPayload): Promise<null> => {
    const request = new Request('finsight_web.contact.subscription.add_subscribers', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  checkEmailsStatus = async (payload: { emails: string[] }):Promise<null> => {
    const request = new Request('finsight_web.contact.subscription.check_subscribers_status', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getTeamsByName = async (payload: { name: string }): Promise<{ id: string, name: string }[]> => {
    const request = new Request('finsight_web.contact.subscription.find_teams_by_name', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  subscriptionRequest = async (payload: { plan: UserPlanType }): Promise<null> => {
    const request = new Request('finsight_web.contact.subscription.create_subscription_request', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };

  getRoles = async (): Promise<{ id: string, name: string }[]> => {
    const request = new Request('finsight_web.contact.subscription.get_roles');
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult().payload;
  };
}

export default ContactRepository;
